import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { BarChart3, CalendarDays, ClipboardCheck, LayoutDashboard, LogOut, Menu, Newspaper, QrCode, Route, ScrollText, Settings, ShieldCheck, Trophy, UserCog, Users, Smartphone } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { Brand } from "@/components/Common";
import { LanguageSelector } from "@/components/LanguageSelector";
import { Topo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const NAV = [
  ["", LayoutDashboard, "Ringkasan", "overview"],
  ["events", CalendarDays, "Event", "events"],
  ["routes", Route, "Jalur", "routes"],
  ["participants", Users, "Peserta", "participants"],
  ["feed", Newspaper, "Feed", "feed"],
  ["lead", ShieldCheck, "Eligibilitas Lead", "lead"],
  ["attendance", ClipboardCheck, "Kehadiran", "attendance"],
  ["analytics", BarChart3, "Analitik", "analytics"],
  ["leaderboard", Trophy, "Leaderboard", "leaderboard"],
  ["donation", QrCode, "Kontribusi Kolektif QRIS", "donation"],
  ["staff", UserCog, "Staf / Keeper", "staff"],
  ["audit", ScrollText, "Audit Log", "audit"],
  ["settings", Settings, "Pengaturan", "settings"],
];

function NavItems({ onPick, prefix = "admin-nav" }) {
  return (
    <>
      {NAV.map(([to, Icon, label, id]) => (
        <NavLink key={id} to={`/admin${to ? `/${to}` : ""}`} end={!to} onClick={onPick} data-testid={`${prefix}-${id}`}
          className={({ isActive }) => cn("flex shrink-0 items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors", isActive ? "bg-[#1A3323] text-[#FDFCF8]" : "text-[#242926]/75 hover:bg-[#E3EDD9]")}>
          <Icon className="h-4 w-4" /> {label}
        </NavLink>
      ))}
      <NavLink to="/keeper" onClick={onPick} data-testid={`${prefix}-keeper`} className="flex shrink-0 items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold text-[#3D6F4B] hover:bg-[#E3EDD9]">
        <Smartphone className="h-4 w-4" /> Buka Mode Keeper
      </NavLink>
    </>
  );
}

export default function AdminLayout() {
  const { user, signOut } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const current = NAV.find(([to]) => (to ? pathname.startsWith(`/admin/${to}`) : pathname === "/admin"));
  const logout = async () => {
    await signOut();
    nav("/");
  };

  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      <aside className="relative z-30 overflow-hidden border-b border-[#1A3323]/10 bg-[#EFEBDD] max-lg:sticky max-lg:top-0 lg:sticky lg:top-0 lg:h-screen lg:w-64 lg:shrink-0 lg:border-b-0 lg:border-r">
        <Topo className="absolute -bottom-10 -left-20 hidden h-72 w-96 text-[#3D6F4B]/15 lg:block" />
        <div className="relative flex items-center justify-between gap-3 p-3 lg:p-5">
          <Brand sub="admin" className="h-12 lg:h-20" />
          <div className="flex items-center gap-2">
            <LanguageSelector />
            <Button
              variant="outline"
              className="h-10 gap-2 rounded-full border-[#1A3323]/20 bg-white px-3 font-semibold text-[#1A3323] lg:hidden"
              onClick={() => setMenuOpen(true)}
              aria-label="Buka menu admin"
              data-testid="admin-mobile-menu-button"
            >
              <Menu className="h-4 w-4" />
              <span className="max-w-[96px] truncate text-xs" data-testid="admin-mobile-current-page">{current?.[2] || "Menu"}</span>
            </Button>
          </div>
        </div>
        <nav className="relative hidden gap-1 px-3 pb-3 lg:flex lg:flex-col">
          <NavItems />
        </nav>
        <div className="hidden px-5 py-4 lg:absolute lg:bottom-0 lg:block lg:w-64">
          <div className="truncate text-xs text-muted-foreground">{user?.email}</div>
          <Button
            variant="ghost"
            className="mt-1 h-9 px-0 text-red-700"
            onClick={logout}
            data-testid="admin-logout-button"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Keluar
          </Button>
        </div>
      </aside>
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className="flex w-[82vw] max-w-xs flex-col overflow-y-auto bg-[#EFEBDD] p-4" data-testid="admin-mobile-menu">
          <SheetHeader className="text-left"><SheetTitle className="text-[#1A3323]">Menu Admin</SheetTitle></SheetHeader>
          <nav className="mt-3 flex flex-col gap-1"><NavItems prefix="admin-mobile-nav" onPick={() => setMenuOpen(false)} /></nav>
          <div className="mt-auto border-t border-[#1A3323]/10 pt-3">
            <div className="truncate text-xs text-muted-foreground">{user?.email}</div>
            <Button variant="ghost" className="mt-1 h-10 w-full justify-start px-3 text-red-700 hover:bg-red-50" onClick={logout} data-testid="admin-mobile-logout-button">
              <LogOut className="mr-2 h-4 w-4" /> Keluar
            </Button>
          </div>
        </SheetContent>
      </Sheet>
      <main className="min-w-0 flex-1 p-4 sm:p-8"><Outlet /></main>
    </div>
  );
}

export const PageTitle = ({ title, desc, children }) => (
  <div className="mb-6 flex flex-wrap items-end justify-between gap-3 rise">
    <div>
      <h1 className="text-3xl font-extrabold tracking-tight text-[#1A3323]">{title}</h1>
      {desc && <p className="mt-1 text-sm text-muted-foreground">{desc}</p>}
    </div>
    {children}
  </div>
);

export const Stat = ({ label, value, sub, testid, accent }) => (
  <div className={cn("rounded-2xl p-4 ring-1 ring-[#1A3323]/10", accent ? "bg-[#1A3323] text-[#FDFCF8]" : "bg-white")}>
    <div className={cn("text-xs font-semibold", accent ? "text-[#FDFCF8]/60" : "text-muted-foreground")}>{label}</div>
    <div className="mt-1 font-mono text-3xl font-extrabold" data-testid={testid}>{value}</div>
    {sub && <div className={cn("text-xs", accent ? "text-[#FDFCF8]/60" : "text-muted-foreground")}>{sub}</div>}
  </div>
);

export const Panel = ({ title, children, className, action }) => (
  <div className={cn("rounded-2xl bg-white p-5 ring-1 ring-[#1A3323]/10", className)}>
    {(title || action) && <div className="mb-3 flex items-center justify-between"><h3 className="font-bold text-[#1A3323]">{title}</h3>{action}</div>}
    {children}
  </div>
);
